/**
 * Variants management table — Inc-15/16 matrix + UC-176 associate + UC-177 disassociate.
 */

import React, { useEffect, useRef } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import AssociateVariantDialog from './AssociateVariantDialog';
import DataTablePagination from '@/components/data-table/DataTablePagination';
import type {
  VariantManagementColumn,
  VariantManagementIdentifier,
  VariantManagementRow,
} from '@/shared/types/variantMapping';
import { formatEveId } from '@/shared/variantMapping/formatEveId';
import {
  variantManagementCellIdentifier,
  variantManagementCellValue,
} from '@/shared/variantMapping/variantManagementColumns';
import {
  ASSOCIATE_MENU_LABEL,
  DISASSOCIATE_DONE_LABEL,
  DISASSOCIATE_MENU_LABEL,
} from '@/shared/variantMapping/variantAssociation';
import DefaultColumnEditor from './DefaultColumnEditor';
import SoldAsOverrideDialog from './SoldAsOverrideDialog';
import BoughtAsOverrideDialog from './BoughtAsOverrideDialog';
import { roleHeadingStyle, roleToggleStyle, SET_DEFAULT_MENU_LABEL, SOLD_AS_TEXT, variantCardStyle } from '@/lib/designations/organisationDefaults';
import { OVERRIDE_BOUGHT_AS_LABEL, OVERRIDE_SOLD_AS_LABEL, RESET_BOTH_LABEL, type SoldAsOption } from '@/lib/designations/rowOverrides';
import { ROW_WARNING_EDGE, ROW_WARNING_FILL, rowLacksSoldOrBought } from '@/lib/designations/warningState';
import './variantMappingDesign.css';

interface VariantsManagementTableProps {
  rows: VariantManagementRow[];
  columns: VariantManagementColumn[];
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onAssociateRequest?: (row: VariantManagementRow) => void;
  associateRow?: VariantManagementRow | null;
  associateError?: string | null;
  associateSubmitting?: boolean;
  onAssociateCancel?: () => void;
  onAssociateConfirm?: (identifierValue: string) => void;
  disassociateRowId?: string | null;
  onEnterDisassociate?: (row: VariantManagementRow) => void;
  onExitDisassociate?: () => void;
  onDisassociateIdentifier?: (
    row: VariantManagementRow,
    identifier: VariantManagementIdentifier,
  ) => void;
  organisationRowCount?: number;
  defaultEditing?: boolean;
  soldAsColumnKey?: string | null;
  boughtAsColumnKey?: string | null;
  onConfigureDefaults?: () => void;
  onSoldAsColumn?: (columnKey: string) => void;
  onBoughtAsColumn?: (columnKey: string) => void;
  onCancelDefaults?: () => void;
  onApplyDefaults?: () => void;
  applyingDefaults?: boolean;
  onOverrideSoldAs?: (row: VariantManagementRow) => void;
  onOverrideBoughtAs?: (row: VariantManagementRow) => void;
  soldAsOverride?: {
    rowId: string;
    contextLabel?: string;
    options: SoldAsOption[];
    resetEnabled: boolean;
    pending?: boolean;
    onUseThis: (option: SoldAsOption) => void;
    onReset: () => void;
    onDone: () => void;
  } | null;
  boughtAsOverride?: {
    rowId: string;
    contextLabel?: string;
    options: SoldAsOption[];
    resetEnabled: boolean;
    pending?: boolean;
    onUseThis: (option: SoldAsOption) => void;
    onReset: () => void;
    onDone: () => void;
  } | null;
  onResetDesignations?: (row: VariantManagementRow) => void;
  soldAsByRow?: Record<string, { columnKey: string | null; missing: boolean; canOverride: boolean; hasOverride?: boolean }>;
  boughtAsByRow?: Record<string, { columnKey: string | null; missing: boolean; canOverride: boolean; hasOverride?: boolean }>;
}

function identityDescription(row: VariantManagementRow): string {
  return row.productDescription?.trim() || '—';
}

function identityCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'identifier' : 'identifiers'}`;
}

const VariantsManagementTable: React.FC<VariantsManagementTableProps> = ({
  rows,
  columns,
  isLoading,
  currentPage,
  totalPages,
  onPageChange,
  onAssociateRequest,
  associateRow = null,
  associateError = null,
  associateSubmitting = false,
  onAssociateCancel,
  onAssociateConfirm,
  disassociateRowId = null,
  onEnterDisassociate,
  onExitDisassociate,
  onDisassociateIdentifier,
  organisationRowCount,
  defaultEditing = false,
  soldAsColumnKey = null,
  boughtAsColumnKey = null,
  onConfigureDefaults,
  onSoldAsColumn,
  onBoughtAsColumn,
  onCancelDefaults,
  onApplyDefaults,
  applyingDefaults = false,
  onOverrideSoldAs,
  onOverrideBoughtAs,
  soldAsOverride = null,
  boughtAsOverride = null,
  onResetDesignations,
  soldAsByRow,
  boughtAsByRow,
}) => {
  const showRowMenu =
    typeof onAssociateRequest === 'function' || typeof onEnterDisassociate === 'function';
  const showTableMenu = typeof onConfigureDefaults === 'function';
  const showActionColumn = showRowMenu || showTableMenu;
  const slotCount = Math.max(columns.length, 1);
  const actionWidth = showActionColumn ? 44 : 0;
  const colSpan = Math.max(columns.length + 1 + (showActionColumn ? 1 : 0) + 1, 1);
  const columnFloor = 230 + slotCount * 178 + actionWidth;
  const showPager = rows.length > 0 && totalPages > 1;
  const openingAssociate = useRef(false);

  useEffect(() => {
    if (!disassociateRowId || !onExitDisassociate) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onExitDisassociate();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disassociateRowId, onExitDisassociate]);

  const rowCount = organisationRowCount ?? rows.length;

  return (
    <div data-testid="variants-management-table">
      {defaultEditing && onCancelDefaults && onApplyDefaults ? (
        <DefaultColumnEditor
          onCancel={onCancelDefaults}
          onApply={onApplyDefaults}
          applying={applyingDefaults}
        />
      ) : null}
      <div className="vm-design__matrix">
        <Table
          className="vm-design__table"
          containerClassName="w-full overflow-auto"
          style={{ width: '100%', minWidth: columnFloor }}
        >
          <TableHeader>
            <TableRow className="vm-design__head-row hover:bg-[#eceff4]">
              <TableHead className="vm-design__identity-head" aria-label="Variant identity" />
              {columns.length === 0 ? (
                <TableHead className="vm-design__slot-head">Identifiers</TableHead>
              ) : (
                columns.map((column) => {
                  const sold = soldAsColumnKey === column.key;
                  const bought = boughtAsColumnKey === column.key;
                  return (
                    <TableHead
                      key={column.key}
                      className="vm-design__slot-head"
                      data-sold-as={sold ? 'true' : undefined}
                      data-bought-as={bought ? 'true' : undefined}
                    >
                      <div className="flex flex-col gap-1">
                        <span className="vm-design__role-label" style={roleHeadingStyle({ sold, bought })}>
                          {column.typeName}
                        </span>
                        {defaultEditing ? (
                          <span className="flex gap-1">
                            <button
                              type="button"
                              data-testid={`sold-as-option-${column.key}`}
                              aria-pressed={sold}
                              className="text-[11px] font-semibold"
                              style={roleToggleStyle({ active: sold, role: 'sold' })}
                              onClick={() => onSoldAsColumn?.(column.key)}
                            >
                              Sold
                            </button>
                            <button
                              type="button"
                              data-testid={`bought-as-option-${column.key}`}
                              aria-pressed={bought}
                              className="text-[11px] font-semibold"
                              style={roleToggleStyle({ active: bought, role: 'bought' })}
                              onClick={() => onBoughtAsColumn?.(column.key)}
                            >
                              Bought
                            </button>
                          </span>
                        ) : null}
                      </div>
                    </TableHead>
                  );
                })
              )}
              <TableHead className="vm-design__filler-head" aria-hidden />
              {showActionColumn ? (
                <TableHead className="vm-design__action-head" aria-label="Table actions">
                  {showTableMenu ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          data-testid="variant-table-menu"
                          aria-label="Table options"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          disabled={rowCount === 0}
                          data-testid="set-default-columns-action"
                          onSelect={() => onConfigureDefaults?.()}
                        >
                          {SET_DEFAULT_MENU_LABEL}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow className="vm-design__body-row">
                <TableCell colSpan={colSpan}>
                  <div className="vm-design__empty">Loading variants...</div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow className="vm-design__body-row">
                <TableCell colSpan={colSpan}>
                  <div className="vm-design__empty">No variants to display.</div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const inDisassociateMode = disassociateRowId === row.variantId;
                const rowWarning = rowLacksSoldOrBought({
                  sold: soldAsByRow?.[row.variantId],
                  bought: boughtAsByRow?.[row.variantId],
                });
                return (
                  <TableRow
                    key={row.variantId}
                    className="vm-design__body-row hover:bg-[#fbfcfe]"
                    data-disassociate-mode={inDisassociateMode ? 'true' : undefined}
                    data-row-warning={rowWarning ? 'true' : undefined}
                    style={
                      rowWarning
                        ? { backgroundColor: ROW_WARNING_FILL, boxShadow: `inset 3px 0 0 ${ROW_WARNING_EDGE}` }
                        : undefined
                    }
                  >
                    <TableCell className="vm-design__identity-cell">
                      <div
                        className="vm-design__identity"
                        data-testid={`variant-identity-${row.variantId}`}
                      >
                        <div className="vm-design__identity-top">
                          <span className="vm-design__identity-eve">{formatEveId(row.eveId)}</span>
                          <span className="vm-design__identity-count">
                            {identityCountLabel(row.linkedIdentifierCount)}
                          </span>
                        </div>
                        <span className="vm-design__identity-description">
                          {identityDescription(row)}
                        </span>
                      </div>
                    </TableCell>
                    {columns.length === 0 ? (
                      <TableCell className="vm-design__slot-cell">
                        <div className="vm-design__cell vm-design__cell--empty" />
                      </TableCell>
                    ) : (
                      columns.map((column) => {
                        const identifier = variantManagementCellIdentifier(row, column);
                        const value = identifier?.value ?? variantManagementCellValue(row, column);
                        const soldCell = soldAsByRow?.[row.variantId];
                        const boughtCell = boughtAsByRow?.[row.variantId];
                        const sold = defaultEditing ? soldAsColumnKey === column.key : soldCell?.columnKey === column.key;
                        const bought = defaultEditing ? boughtAsColumnKey === column.key : boughtCell?.columnKey === column.key;
                        const missing = defaultEditing
                          ? (sold || bought) && value.trim() === ''
                          : Boolean((sold && soldCell?.missing) || (bought && boughtCell?.missing));
                        const card = variantCardStyle({ sold, bought, missing });
                        return (
                          <TableCell
                            key={`${row.variantId}-${column.key}`}
                            className="vm-design__slot-cell"
                            data-sold-as={sold ? 'true' : undefined}
                            data-bought-as={bought ? 'true' : undefined}
                            data-missing-designation={missing ? 'true' : undefined}
                          >
                            {value ? (
                              <div className="vm-design__cell" style={card}>
                                <span
                                  className="vm-design__cell-value"
                                  style={sold && !missing ? { color: SOLD_AS_TEXT } : undefined}
                                >
                                  {value}
                                </span>
                                {inDisassociateMode && identifier ? (
                                  <button
                                    type="button"
                                    className="vm-design__disassociate-x"
                                    data-testid={`disassociate-x-${row.variantId}-${identifier.linkId ?? `${identifier.type}-${identifier.value}`}`}
                                    aria-label={`Disassociate ${identifier.value}`}
                                    title="Disassociate this variant"
                                    onClick={() => onDisassociateIdentifier?.(row, identifier)}
                                  >
                                    ×
                                  </button>
                                ) : null}
                              </div>
                            ) : (
                              <div className="vm-design__cell vm-design__cell--empty" style={card} />
                            )}
                          </TableCell>
                        );
                      })
                    )}
                    <TableCell className="vm-design__filler-cell" />
                    {showActionColumn && !showRowMenu ? <TableCell className="vm-design__action-cell" /> : null}
                    {showRowMenu ? (
                      <TableCell className="vm-design__action-cell">
                        <Popover
                          open={
                            associateRow?.variantId === row.variantId
                            || soldAsOverride?.rowId === row.variantId
                            || boughtAsOverride?.rowId === row.variantId
                          }
                          onOpenChange={(next) => {
                            if (next || openingAssociate.current) return;
                            if (soldAsOverride?.rowId === row.variantId) soldAsOverride.onDone();
                            else if (boughtAsOverride?.rowId === row.variantId) boughtAsOverride.onDone();
                            else onAssociateCancel?.();
                          }}
                        >
                          <PopoverAnchor asChild>
                            <div className="vm-design__action-anchor">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="vm-design__row-menu-trigger !h-7 !w-7 !text-[#667085] hover:!bg-[#eff1f5] hover:!text-[#667085]"
                                    data-testid={`variant-row-menu-${row.variantId}`}
                                    aria-label="Variant row options"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="vm-design__row-menu w-[240px] rounded-[10px] border-[#d7dbe3] bg-white p-0 text-[#0f1222] shadow-[0_18px_44px_rgba(16,24,40,0.22)]"
                                  onCloseAutoFocus={(event) => {
                                    if (openingAssociate.current) event.preventDefault();
                                  }}
                                >
                                  <DropdownMenuLabel className="vm-design__row-menu-caption">
                                    {formatEveId(row.eveId)}
                                  </DropdownMenuLabel>
                                  {onOverrideSoldAs ? (
                                    <DropdownMenuItem
                                      className="vm-design__row-menu-item"
                                      disabled={!soldAsByRow?.[row.variantId]?.canOverride}
                                      data-testid={`override-sold-as-action-${row.variantId}`}
                                      onSelect={() => {
                                        openingAssociate.current = true;
                                        onOverrideSoldAs(row);
                                        window.setTimeout(() => {
                                          openingAssociate.current = false;
                                        }, 200);
                                      }}
                                    >
                                      <span className="vm-design__menu-swatch vm-design__menu-swatch--sold" aria-hidden />
                                      {OVERRIDE_SOLD_AS_LABEL}
                                    </DropdownMenuItem>
                                  ) : null}
                                  {onOverrideBoughtAs ? (
                                    <DropdownMenuItem
                                      className="vm-design__row-menu-item"
                                      disabled={!boughtAsByRow?.[row.variantId]?.canOverride}
                                      data-testid={`override-bought-as-action-${row.variantId}`}
                                      onSelect={() => {
                                        openingAssociate.current = true;
                                        onOverrideBoughtAs(row);
                                        window.setTimeout(() => {
                                          openingAssociate.current = false;
                                        }, 200);
                                      }}
                                    >
                                      <span className="vm-design__menu-swatch vm-design__menu-swatch--bought" aria-hidden />
                                      {OVERRIDE_BOUGHT_AS_LABEL}
                                    </DropdownMenuItem>
                                  ) : null}
                                  {onResetDesignations ? (
                                    <DropdownMenuItem
                                      className="vm-design__row-menu-item"
                                      disabled={
                                        !soldAsByRow?.[row.variantId]?.hasOverride &&
                                        !boughtAsByRow?.[row.variantId]?.hasOverride
                                      }
                                      data-testid={`reset-designations-action-${row.variantId}`}
                                      onSelect={() => onResetDesignations(row)}
                                    >
                                      {RESET_BOTH_LABEL}
                                    </DropdownMenuItem>
                                  ) : null}
                                  {onAssociateRequest ? (
                                    <DropdownMenuItem
                                      className="vm-design__row-menu-item"
                                      data-testid={`associate-variant-action-${row.variantId}`}
                                      onSelect={() => {
                                        openingAssociate.current = true;
                                        onAssociateRequest(row);
                                        window.setTimeout(() => {
                                          openingAssociate.current = false;
                                        }, 200);
                                      }}
                                    >
                                      {ASSOCIATE_MENU_LABEL}
                                    </DropdownMenuItem>
                                  ) : null}
                                  {inDisassociateMode && onExitDisassociate ? (
                                    <DropdownMenuItem
                                      className="vm-design__row-menu-item"
                                      data-testid={`done-disassociate-action-${row.variantId}`}
                                      onSelect={() => onExitDisassociate()}
                                    >
                                      {DISASSOCIATE_DONE_LABEL}
                                    </DropdownMenuItem>
                                  ) : onEnterDisassociate ? (
                                    <DropdownMenuItem
                                      className="vm-design__row-menu-item vm-design__row-menu-item--danger"
                                      data-testid={`disassociate-variant-action-${row.variantId}`}
                                      onSelect={() => onEnterDisassociate(row)}
                                    >
                                      {DISASSOCIATE_MENU_LABEL}
                                    </DropdownMenuItem>
                                  ) : null}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </PopoverAnchor>
                          <PopoverContent
                            align="end"
                            side="bottom"
                            sideOffset={6}
                            className="w-auto border-0 bg-transparent p-0 shadow-none"
                            onOpenAutoFocus={(event) => {
                              event.preventDefault();
                              const input = document.getElementById('associate-variant-identifier');
                              if (input instanceof HTMLInputElement) input.focus();
                            }}
                            onCloseAutoFocus={(event) => event.preventDefault()}
                            onFocusOutside={(event) => event.preventDefault()}
                            onInteractOutside={(event) => event.preventDefault()}
                            onPointerDownOutside={(event) => event.preventDefault()}
                          >
                            {soldAsOverride?.rowId === row.variantId ? (
                              <SoldAsOverrideDialog
                                open
                                contextLabel={soldAsOverride.contextLabel}
                                options={soldAsOverride.options}
                                resetEnabled={soldAsOverride.resetEnabled}
                                pending={soldAsOverride.pending}
                                onUseThis={soldAsOverride.onUseThis}
                                onReset={soldAsOverride.onReset}
                                onDone={soldAsOverride.onDone}
                              />
                            ) : null}
                            {boughtAsOverride?.rowId === row.variantId ? (
                              <BoughtAsOverrideDialog
                                open
                                contextLabel={boughtAsOverride.contextLabel}
                                options={boughtAsOverride.options}
                                resetEnabled={boughtAsOverride.resetEnabled}
                                pending={boughtAsOverride.pending}
                                onUseThis={boughtAsOverride.onUseThis}
                                onReset={boughtAsOverride.onReset}
                                onDone={boughtAsOverride.onDone}
                              />
                            ) : null}
                            {associateRow?.variantId === row.variantId ? (
                              <AssociateVariantDialog
                                open
                                row={row}
                                isSubmitting={associateSubmitting}
                                errorMessage={associateError}
                                onCancel={() => onAssociateCancel?.()}
                                onConfirm={(value) => onAssociateConfirm?.(value)}
                              />
                            ) : null}
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {showPager && (
          <div className="vm-design__pager">
            <p className="vm-design__pager-label">
              Showing page {currentPage} of {totalPages}
            </p>
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default VariantsManagementTable;
